import { fetcher } from '@ordermentum/ui-shared/swr/fetchers';
import { locationT, OutstandingInvoices } from '@ordermentum/ui-types';
import SkeletonLoader from '@ordermentum/ui-shared/components/Skeleton/Skeleton';
import { TableSkeleton } from '@ordermentum/ui-shared/components/Skeleton';
import StandaloneFooter from '@ordermentum/ui-shared/components/StandaloneFooter';
import qs from 'qs';
import React, { useState } from 'react';
import useSWR from 'swr';
import Button, {
  LinkButton,
} from '@ordermentum/ui-shared/components/Button/Button';
import axios from 'axios';
import { NOTIFICATION_DELAY } from '@ordermentum/ui-constants';
import { connect } from 'react-redux';
import dispatcher, {
  Dispatcher,
} from '@ordermentum/ui-shared/redux/dispatcher';
import { Prompt } from '@ordermentum/ui-shared/components/Modal';
import { AnchorNavigation } from '@ordermentum/ui-shared/components/Navigation';
import history from '../../lib/history';
import OutstandingInvoicesTable from './OutstandingInvoicesTable';

type Props = {
  params: {
    retailerId: string;
  };
  location: locationT;
  dispatch: Dispatcher;
};

const OutstandingInvoicesPage = ({ location, params, dispatch }: Props) => {
  const { retailerId } = params;
  const { query } = location;
  const { paymentRequestId, token, invoiceId, userId } = query;
  const [showPayModal, setShowPayModal] = useState<boolean>(false);
  useState<boolean>(false);
  const [isPaying, setIsPaying] = useState<boolean>(false);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const {
    data: outstandingInvoices,
    error,
    isLoading,
  } = useSWR<OutstandingInvoices>(
    `/v1/payment-requests/${paymentRequestId}/outstanding-invoices?${qs.stringify(
      { token }
    )}`,
    fetcher()
  );

  const handleBulkPay = async () => {
    if (isPaying) return;
    setIsPaying(true);
    try {
      await axios.post(
        `/v1/payment-requests/${paymentRequestId}/bulk-invoice-pay`,
        {
          token,
          invoiceIds: selectedInvoices,
        }
      );
      await dispatch({
        type: '',
        notificationPayload: 'Invoices successfully paid',
        notificationDelay: NOTIFICATION_DELAY,
        notificationType: 'success',
      });
      history.push({
        pathname: `/payments/${retailerId}/auth`,
        query: {
          paymentRequestId,
          invoiceId,
          userId,
          token,
        },
      });
    } catch {
      await dispatch({
        type: '',
        notificationPayload: 'Error in paying invoices',
        notificationDelay: NOTIFICATION_DELAY,
        notificationType: 'error',
      });
    }
    setIsPaying(false);
  };

  const expireToken = async () => {
    await axios.post(
      `/v1/payment-requests/${paymentRequestId}/bulk-invoice-pay`,
      {
        token,
        invoiceIds: [],
      }
    );
  };

  if ((error || !outstandingInvoices) && !isLoading)
    return (
      <div className="flex h-screen flex-col items-center justify-center text-lg font-bold">
        <div>Failed to load required data</div>
        <div>
          <AnchorNavigation url="/" variant="label">
            Go home
          </AnchorNavigation>
        </div>
      </div>
    );

  const totalDue = outstandingInvoices?.data?.outstandingTotal ?? 0;
  const invoices = outstandingInvoices?.data?.invoices ?? [];
  const totalOutstandingInvoices = invoices?.length;

  return (
    <div className="flex h-full flex-col overflow-y-scroll bg-regular ">
      <div className="my-16 flex max-w-3xl flex-1 flex-col self-center">
        <div className="flex w-4/5 flex-col items-center gap-y-5 self-center">
          <h1 className="text-center font-heading text-4xl font-bold text-strong">
            Do you want to pay these outstanding invoices?
          </h1>
          <div className="flex flex-col items-center">
            <p className="font-body font-normal text-subtle">
              Outstanding total amount
            </p>
            {isLoading ? (
              <SkeletonLoader widthClass="w-96" />
            ) : (
              <h2 className="font-heading text-4xl font-bold text-strong">
                ${totalDue}
              </h2>
            )}
          </div>
          {!isLoading && (
            <p className="font-body font-normal text-strong">
              According to our records, you have {totalOutstandingInvoices}{' '}
              outstanding invoices. Please see details below:
            </p>
          )}
        </div>
        <div className="flex flex-col gap-10">
          {isLoading ? (
            <TableSkeleton columns={6} rows={10} />
          ) : (
            <OutstandingInvoicesTable
              invoices={invoices}
              retailerId={retailerId}
              setSelectedInvoices={setSelectedInvoices}
              selectedInvoices={selectedInvoices}
            />
          )}

          <div className="flex justify-center gap-10">
            <LinkButton
              variant="secondary"
              to={`/payments/${retailerId}/auth?${qs.stringify(query)}`}
              disabled={isPaying}
              onClick={expireToken}
            >
              Maybe later
            </LinkButton>
            <Button
              variant="primary"
              onClick={() => {
                setShowPayModal(true);
              }}
              disabled={selectedInvoices.length === 0}
              loading={isPaying}
            >
              Pay selected invoices now
            </Button>
          </div>
        </div>
      </div>
      <StandaloneFooter />
      <Prompt
        title="Confirm payment"
        isOpen={showPayModal}
        actionLabel="Confirm payment"
        onRequestClose={() => {
          setShowPayModal(false);
        }}
        onAccept={handleBulkPay}
        loading={isPaying}
      >
        Are you sure you want to pay the selected invoices
      </Prompt>
    </div>
  );
};

const mapDispatchToProps = {
  dispatch: dispatcher,
};

export default connect(null, mapDispatchToProps)(OutstandingInvoicesPage);
